import { type Fragment, Node as ProseMirrorNode, type Schema, Slice } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import type { JSONContent } from "@tiptap/react";

/**
 * Shared insertion mechanics for the paste plugins that re-parse clipboard text through the
 * Markdown converter ({@link import("./external-markdown-paste").ExternalMarkdownPaste} and
 * {@link import("./mention-token-paste").MentionTokenPaste}). Both produce ProseMirror JSON and
 * then have to put it somewhere; only *where* differs, so the transaction shapes live here.
 */

/** JSON → node against the surface's own schema. `null` when the node has no place in it. */
export function createNodeFromJson(schema: Schema, node: JSONContent): ProseMirrorNode | null {
  try {
    return ProseMirrorNode.fromJSON(schema, node);
  } catch {
    return null;
  }
}

/**
 * Replaces the textblock the cursor sits in with pasted *block* content — what a pasted list
 * or heading wants, since it cannot live inside the paragraph it landed in.
 */
export function replaceWithPastedBlocks(view: EditorView, content: Fragment): boolean {
  try {
    const selection = view.state.selection;
    const transaction =
      selection.$from.sameParent(selection.$to) && selection.$from.parent.isTextblock
        ? view.state.tr
            .replaceWith(
              selection.$from.before(selection.$from.depth),
              selection.$from.after(selection.$from.depth),
              content
            )
            .scrollIntoView()
        : view.state.tr.replaceSelection(new Slice(content, 0, 0)).scrollIntoView();
    view.dispatch(transaction);
    return true;
  } catch {
    return false;
  }
}

/**
 * Inserts *inline* content at the current position. A single pasted paragraph is prose, not a
 * block: it has to splice into the sentence the caret is in rather than break it in two.
 */
export function insertPastedInline(view: EditorView, content: Fragment): boolean {
  try {
    view.dispatch(view.state.tr.replaceSelection(new Slice(content, 0, 0)).scrollIntoView());
    return true;
  } catch {
    return false;
  }
}
